<template>
  <div class="ext-dashboard">
    <template v-if="currentOption">

      <!-- ── Page header (breadcrumb strip) ───────────────── -->
      <div class="ext-dashboard__header">
        <div class="ext-dashboard__header-left">
          <v-icon class="ext-dashboard__header-icon" size="16">{{ currentOption.icon }}</v-icon>
          <h1 class="ext-dashboard__title">{{ currentOption.label }}</h1>
          <span v-if="currentOption.subtitle" class="ext-dashboard__subtitle">
            {{ currentOption.subtitle }}
          </span>
        </div>
      </div>

      <!-- ── Section content ─────────────────────────────── -->
      <component :is="currentComponent" v-bind="componentProps" />

    </template>
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from "vue";
import axios from "axios";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import Employees from "@/components/Employees.vue";
import EmployeeDetailsExt from "@/components/EmployeeDetailsExt.vue";
import EmployeeHolidays from "@/components/holiday/EmployeeHolidays.vue";
import { parseCompanyId } from "@/components/dashboard/utils.mjs";
import EmployeeDetailsExtView from "@/components/EmployeeDetailsExtView.vue";
import UserAccountSettings from "@/components/UserAccountSettings.vue";
import EmployeeVacationsTeam from "@/components/EmployeeVacationsTeam.vue";
import EmployeeTimesheet from "@/components/EmployeeTimesheet.vue";
import EmployeeFiles from "@/components/employee/EmployeeFiles.vue";
import ChecklistPanel from "@/components/checklist/ChecklistPanel.vue";
import EmployeeFinance from "@/components/payroll/EmployeeFinance.vue";

export default {
  name: "ExternalDashboard",
  components: {
    Employees,
    EmployeeDetailsExt,
    EmployeeHolidays,
    EmployeeDetailsExtView,
    UserAccountSettings,
    EmployeeVacationsTeam,
    EmployeeTimesheet,
    EmployeeFiles,
    ChecklistPanel,
    EmployeeFinance,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { t } = useI18n();
    const companyId = ref(parseCompanyId(localStorage.getItem("user-company-id")));
    const employeeId = ref(parseCompanyId(localStorage.getItem("user-employee-id")));
    const userRole = ref((localStorage.getItem("user-role") || "").toLowerCase());
    const rolesSet = computed(() => {
      const raw = localStorage.getItem("user-roles");
      let parsed = [];
      try {
        parsed = JSON.parse(raw || "[]");
      } catch (e) {
        parsed = [];
      }
      const set = new Set();
      parsed.forEach((entry) => {
        if (!entry) return;
        const key = entry.key || entry.role || entry.name || entry;
        if (key) set.add(key.toLowerCase());
      });
      const legacy = (localStorage.getItem("user-role") || "").toLowerCase();
      if (legacy) set.add(legacy);
      return set;
    });
    const loadingCompany = ref(false);

    const fetchCompanyFromProfile = async () => {
      loadingCompany.value = true;
      try {
        const { data } = await axios.get("/me");
        const fetchedCompanyId = parseCompanyId(data?.company_id);
        const fetchedEmployeeId = parseCompanyId(data?.employee_id);
        const fetchedRole = (data?.role || data?.user_role || "").toLowerCase();

        if (fetchedCompanyId !== null) {
          companyId.value = fetchedCompanyId;
          localStorage.setItem("user-company-id", String(fetchedCompanyId));
        } else {
          companyId.value = null;
          localStorage.removeItem("user-company-id");
        }

        if (fetchedEmployeeId !== null) {
          employeeId.value = fetchedEmployeeId;
          localStorage.setItem("user-employee-id", String(fetchedEmployeeId));
        } else {
          employeeId.value = null;
          localStorage.removeItem("user-employee-id");
        }

        if (fetchedRole) {
          userRole.value = fetchedRole;
          localStorage.setItem("user-role", fetchedRole);
        }
      } catch (error) {
        console.error("Eroare la determinarea companiei utilizatorului:", error);
      } finally {
        loadingCompany.value = false;
      }
    };

    const subordinateViewId = ref(null);

    const menuOptions = computed(() => {
      const options = [
        {
          id: "profile-overview",
          label: t("externalDashboard.menuItems.profile.label"),
          subtitle: t("externalDashboard.menuItems.profile.subtitle"),
          icon: "mdi-account",
          component: "EmployeeDetailsExt",
        },
        {
          id: "my-holidays",
          label: t("externalDashboard.menuItems.myHolidays.label"),
          subtitle: t("externalDashboard.menuItems.myHolidays.subtitle"),
          icon: "mdi-beach",
          component: "EmployeeHolidays",
        },
        {
          id: "my-checklist",
          label: t("externalDashboard.menuItems.checklist.label"),
          subtitle: t("externalDashboard.menuItems.checklist.subtitle"),
          icon: "mdi-format-list-checks",
          component: "ChecklistPanel",
        },
        {
          id: "my-files",
          label: t("externalDashboard.menuItems.files.label"),
          subtitle: t("externalDashboard.menuItems.files.subtitle"),
          icon: "mdi-folder-account",
          component: "EmployeeFiles",
        },
        {
          id: "finance",
          label: t("externalDashboard.menuItems.finance.label"),
          subtitle: t("externalDashboard.menuItems.finance.subtitle"),
          icon: "mdi-cash-multiple",
          component: "EmployeeFinance",
        },
        {
          id: "my-timesheet",
          label: t("externalDashboard.menuItems.timesheet.label"),
          subtitle: t("externalDashboard.menuItems.timesheet.subtitle"),
          icon: "mdi-clock-outline",
          component: "EmployeeTimesheet",
        },
      ];

      if (rolesSet.value.has("vacation-manager") || rolesSet.value.has("vacation-director")) {
        options.splice(2, 0, {
          id: "team-holidays",
          label: t("externalDashboard.menuItems.teamHolidays.label"),
          subtitle: t("externalDashboard.menuItems.teamHolidays.subtitle"),
          icon: "mdi-account-multiple-check",
          component: "EmployeeVacationsTeam",
        });
      }

      if (subordinateViewId.value) {
        options.push({
          id: "subordinate-details",
          label: t("externalDashboard.menuItems.subordinate.label"),
          subtitle: t("externalDashboard.menuItems.subordinate.subtitle"),
          icon: "mdi-account-group",
          component: "EmployeeDetailsExtView",
        });
      }

      if (rolesSet.value.has("vacation-director")) {
        options.push({
          id: "employee-management",
          label: t("externalDashboard.menuItems.employeeManagement.label"),
          subtitle: t("externalDashboard.menuItems.employeeManagement.subtitle"),
          icon: "mdi-account-multiple-outline",
          component: "Employees",
        });
      }

      options.push({
        id: "main-dashboard",
        label: t("externalDashboard.menuItems.mainDashboard.label"),
        subtitle: t("externalDashboard.menuItems.mainDashboard.subtitle"),
        icon: "mdi-view-grid-plus-outline",
        component: null,
      });

      options.push({
        id: "user-account",
        label: t("externalDashboard.menuItems.userAccount.label"),
        subtitle: t("externalDashboard.menuItems.userAccount.subtitle"),
        icon: "mdi-account-key",
        component: "UserAccountSettings",
      });

      return options;
    });

    const timesheetCanManage = computed(() =>
      ["admin", "miniadmin", "hr", "vacation-hr", "vacation-manager", "vacation-director", "vacation-user", "vacation-default"].some(
        (r) => rolesSet.value.has(r)
      )
    );

    const selectedOption = ref(null);
    const contentReady = ref(false);

    // Sync selectedOption from router query param (set by NavigationDrawer)
    const applyQueryOption = () => {
      const opt = route.query?.option;
      const optId = typeof opt === "string" ? opt.toLowerCase() : null;
      if (optId && menuOptions.value.some((o) => o.id === optId)) {
        if (selectedOption.value !== optId) {
          selectOption(optId);
        }
      }
    };

    watch(
      menuOptions,
      (options) => {
        if (!options.length) {
          selectedOption.value = null;
          return;
        }
        // Apply query param first, fallback to first option
        const opt = route.query?.option;
        const optId = typeof opt === "string" ? opt.toLowerCase() : null;
        if (optId && options.some((o) => o.id === optId)) {
          selectedOption.value = optId;
        } else if (!options.some((opt) => opt.id === selectedOption.value)) {
          selectedOption.value = options[0].id;
        }
      },
      { immediate: true }
    );

    watch(() => route.query?.option, () => {
      applyQueryOption();
    });

    const currentOption = computed(() => {
      if (!selectedOption.value) return null;
      return menuOptions.value.find((option) => option.id === selectedOption.value) || null;
    });

    const currentComponent = computed(() => currentOption.value?.component || null);

    const componentProps = computed(() => {
      if (currentComponent.value === "Employees") {
        return {
          companyIdFilter: companyId.value,
          showCompanySelect: false,
          autoFetch: false,
          allowManagement: false,
          openInDashboard: true,
        };
      }
      if (currentComponent.value === "EmployeeDetailsExt") {
        return {
          employeeId: employeeId.value,
        };
      }
      if (currentComponent.value === "EmployeeHolidays") {
        return {
          employeeId: employeeId.value,
        };
      }
      if (currentComponent.value === "ChecklistPanel") {
        return {
          mode: "employee",
          companyId: companyId.value,
          employeeId: employeeId.value,
        };
      }
      if (currentComponent.value === "EmployeeTimesheet") {
        return {
          employeeId: employeeId.value,
          canManage: timesheetCanManage.value,
          title: t("externalDashboard.menuItems.timesheet.label"),
        };
      }
      if (currentComponent.value === "EmployeeFinance") {
        const targetEmployeeId = subordinateViewId.value || employeeId.value;
        return {
          employeeId: targetEmployeeId,
          companyId: companyId.value,
          canAddBonus: false,
          onlyBonuses: true,
          allowCompanyFallback: false,
        };
      }
      if (currentComponent.value === "EmployeeVacationsTeam") {
        return {
          managerId: employeeId.value,
          companyId: companyId.value,
          role: userRole.value,
        };
      }
      if (currentComponent.value === "EmployeeDetailsExtView") {
        return {
          employeeId: subordinateViewId.value,
        };
      }
      if (currentComponent.value === "EmployeeFiles") {
        return {
          employeeId: employeeId.value,
        };
      }
      return {};
    });

    const selectOption = (optionId) => {
      if (optionId === "main-dashboard") {
        router.push("/");
        return;
      }
      // If user leaves the subordinate view, drop it from the menu
      if (!["subordinate-details", "finance"].includes(optionId)) {
        subordinateViewId.value = null;
      }
      selectedOption.value = optionId;
      contentReady.value = false;
      nextTick(() => {
        contentReady.value = true;
      });
    };

    const handleOptionClick = (optionId) => {
      selectOption(optionId);
    };

    const handleNavigateEvent = (evt) => {
      const optionId = evt?.detail?.optionId;
      if (!optionId) return;
      if (menuOptions.value.some((opt) => opt.id === optionId)) {
        selectOption(optionId);
      }
    };

    const handleOpenSubordinate = (evt) => {
      const id = evt?.detail?.employeeId;
      if (!id) return;
      subordinateViewId.value = id;
      selectOption("subordinate-details");
    };

    onMounted(() => {
      fetchCompanyFromProfile();
      window.addEventListener("ext-dashboard:navigate", handleNavigateEvent);
      window.addEventListener("ext-dashboard:open-subordinate", handleOpenSubordinate);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("ext-dashboard:navigate", handleNavigateEvent);
      window.removeEventListener("ext-dashboard:open-subordinate", handleOpenSubordinate);
    });

    return {
      menuOptions,
      selectedOption,
      currentOption,
      currentComponent,
      selectOption,
      handleOptionClick,
      contentReady,
      componentProps,
      loadingCompany,
      subordinateViewId,
      companyId,
      employeeId,
      timesheetCanManage,
      t,
    };
  },
};
</script>

<style src="./ExternalDashboard.styles.css" />
